import React, {
  ReactNode,
  useEffect,
  useState,
} from 'react';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from '@hello-pangea/dnd';
import {
  reorder,
} from 'utils/array';

type Props<T> = {
  items: T[];
  getValue: (val: T) => number | string;
  renderOption: (val: T) => ReactNode;
  onChange?: (items: T[]) => void;
  itemSpacing?: number;
};

export default function DraggableList<T>({
  items,
  getValue,
  renderOption,
  onChange,
  itemSpacing = 10,
}: Props<T>) {
  const [listItems, setListItems] = useState<T[]>([]);

  useEffect(() => {
    setListItems(items || []);
  }, [items]);

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const items = reorder(
      listItems,
      result.source.index,
      result.destination.index,
    );

    setListItems(items);

    if (typeof onChange === 'function') {
      onChange(items);
    }
  };

  return listItems.length > 0 ? (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable">
        {(provided) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            style={{
              flex: 1,
            }}
          >
            {listItems.map((item: T, index: number) => (
              <Draggable
                key={getValue(item)}
                draggableId={getValue(item)?.toString()}
                index={index}
              >
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    style={{
                      marginTop: `${itemSpacing}px`,
                      ...provided.draggableProps.style,
                    }}
                  >
                    {renderOption(item)}
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  ) : null;
}
